import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from 'react-bootstrap';
import PageTransition from '../components/PageTransition';
import '../styles/AppointmentStepper.css';

const APPOINTMENT_ENDPOINT =
  process.env.REACT_APP_APPOINTMENT_ENDPOINT || '/api/appointment.php';

// ─── Constants ────────────────────────────────────────────────────────────────

const businessHours = ['08:00','09:00','10:00','11:00','13:00','14:00','15:00','16:00','17:00'];
const fridayHours   = ['08:00','09:00','10:00','11:00'];

const serviceOptions = [
  { value: 'inspektion', label: 'Inspektion' },
  { value: 'wartung',    label: 'Wartung' },
  { value: 'reparatur',  label: 'Reparatur' },
  { value: 'diagnose',   label: 'Diagnose' },
  { value: 'unfall',     label: 'Unfallschaden' },
  { value: 'andere',     label: 'Andere' },
];

const MONTHS = ['Januar','Februar','März','April','Mai','Juni',
                'Juli','August','September','Oktober','November','Dezember'];
const DOW_LABELS = ['Mo','Di','Mi','Do','Fr','Sa','So'];

const STEPS = [
  { label: 'Über Sie' },
  { label: 'Fahrzeug' },
  { label: 'Bestätigung' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormData {
  isFirstTimeCustomer: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bestandskundeContact: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  licensePlate: string;
  serviceType: string;
  preferredDate: string; // YYYY-MM-DD
  preferredTime: string;
  additionalNotes: string;
}

type FieldErrors = Partial<Record<keyof FormData, string>>;
type ShakeMap    = Partial<Record<keyof FormData, boolean>>;

// ─── Date helpers ─────────────────────────────────────────────────────────────

function getMinDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 14);
  return d;
}

const isWeekday   = (d: Date) => { const n = d.getDay(); return n !== 0 && n !== 6; };
const isFridayDay = (d: Date) => d.getDay() === 5;

function isSelectable(d: Date): boolean {
  const cmp = new Date(d); cmp.setHours(0,0,0,0);
  return isWeekday(cmp) && cmp >= getMinDate();
}

function nextSelectable(from: Date): Date {
  const d = new Date(from); d.setHours(0,0,0,0);
  while (!isSelectable(d)) d.setDate(d.getDate() + 1);
  return d;
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function isoToDisplay(iso: string): string {
  if (!iso) return '';
  const [y, m, day] = iso.split('-');
  return `${day}.${m}.${y}`;
}

function parseDisplay(s: string): Date | null {
  const match = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;
  const d = new Date(+match[3], +match[2] - 1, +match[1]);
  return isNaN(d.getTime()) ? null : d;
}

function formatDateGerman(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  const days = ['So','Mo','Di','Mi','Do','Fr','Sa'];
  return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
}

// ─── Animation variants ───────────────────────────────────────────────────────

const makeStepVariants = (dir: number) => ({
  initial:  { opacity: 0, x: dir * 40 },
  animate:  { opacity: 1, x: 0, transition: { duration: 0.3,  ease: 'easeOut' as const } },
  exit:     { opacity: 0, x: dir * -40, transition: { duration: 0.25, ease: 'easeIn'  as const } },
});

const fieldGroupVariants = {
  initial:  { opacity: 0, height: 0 },
  animate:  { opacity: 1, height: 'auto', transition: { duration: 0.3,  ease: 'easeOut' as const } },
  exit:     { opacity: 0, height: 0,      transition: { duration: 0.2,  ease: 'easeIn'  as const } },
};

// ─── FloatingInput ────────────────────────────────────────────────────────────

interface FloatingInputProps {
  label: string;
  required?: boolean;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  shake?: boolean;
  placeholder?: string;
  as?: 'textarea';
}

const FloatingInput: React.FC<FloatingInputProps> = ({
  label, required, type = 'text', value, onChange, onBlur, error, shake, placeholder, as,
}) => {
  const [focused, setFocused] = useState(false);
  const raised = focused || value.length > 0;

  const sharedProps = {
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value),
    onFocus:  () => setFocused(true),
    onBlur:   () => { setFocused(false); onBlur?.(); },
    placeholder: focused ? (placeholder || '') : '',
    style: error ? { borderBottomColor: 'var(--color-brand)' } as React.CSSProperties : undefined,
  };

  return (
    <motion.div
      className="floating-field"
      animate={shake ? { x: [0,-6,6,-4,4,0] } : { x: 0 }}
      transition={shake ? { duration: 0.35 } : {}}
    >
      {as === 'textarea'
        ? <textarea {...sharedProps} rows={4} />
        : <input type={type} {...sharedProps} />
      }
      <span className={`floating-label${raised ? ' raised' : ''}`}>
        {label}{required && <span className="req"> *</span>}
      </span>
      {error && <div className="field-error">{error}</div>}
    </motion.div>
  );
};

// ─── CustomerToggle ───────────────────────────────────────────────────────────

const CustomerToggle: React.FC<{ isNew: boolean; onChange: (v: boolean) => void }> = ({ isNew, onChange }) => (
  <div className="customer-toggle">
    <button type="button" className={isNew  ? 'active' : ''} onClick={() => onChange(true)}>
      Neukunde
    </button>
    <button type="button" className={!isNew ? 'active' : ''} onClick={() => onChange(false)}>
      Bestandskunde
    </button>
  </div>
);

// ─── CustomCalendar ───────────────────────────────────────────────────────────

interface CalendarProps {
  selectedIso: string;
  onSelect: (iso: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const CustomCalendar: React.FC<CalendarProps> = ({ selectedIso, onSelect, onClose, anchorRef }) => {
  const minDate  = getMinDate();
  const seedDate = selectedIso ? new Date(selectedIso + 'T12:00:00') : new Date(minDate);

  const [viewYear,  setViewYear]  = useState(seedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(seedDate.getMonth());
  const panelRef = useRef<HTMLDivElement>(null);

  const handleOutsideMouseDown = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (
      panelRef.current  && !panelRef.current.contains(target) &&
      anchorRef.current && !anchorRef.current.contains(target)
    ) {
      onClose();
    }
  }, [onClose, anchorRef]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
  }, [handleOutsideMouseDown]);

  const today = new Date(); today.setHours(0,0,0,0);

  // Build grid cells
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  let startDow = firstOfMonth.getDay(); // 0=Sun
  startDow = startDow === 0 ? 6 : startDow - 1; // convert to Mon=0
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const goMonth = (delta: number) => {
    let m = viewMonth + delta, y = viewYear;
    if (m < 0)  { m = 11; y--; }
    if (m > 11) { m = 0;  y++; }
    setViewMonth(m); setViewYear(y);
  };

  const prevDisabled = () => new Date(viewYear, viewMonth, 0) < minDate;

  const selectedDate = selectedIso ? new Date(selectedIso + 'T12:00:00') : null;

  return (
    <motion.div
      ref={panelRef}
      className="calendar-panel"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.18 }}
    >
      <div className="cal-nav">
        <button type="button" className="cal-nav-btn" onClick={() => goMonth(-1)} disabled={prevDisabled()}>←</button>
        <span className="cal-month-label">{MONTHS[viewMonth]} {viewYear}</span>
        <button type="button" className="cal-nav-btn" onClick={() => goMonth(1)}>→</button>
      </div>

      <div className="cal-grid">
        {DOW_LABELS.map(d => <div key={d} className="cal-dow">{d}</div>)}
        {cells.map((date, i) => {
          if (!date) return <div key={`e${i}`} className="cal-day cal-other" />;
          const disabled  = !isSelectable(date);
          const isToday   = date.toDateString() === today.toDateString();
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
          const cls = ['cal-day',
            disabled  ? 'cal-disabled'  : '',
            isToday   ? 'cal-today'     : '',
            isSelected ? 'cal-selected' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={`d${date.getDate()}`}
              type="button"
              className={cls}
              onClick={() => { if (!disabled) { onSelect(toIso(date)); onClose(); } }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

// ─── DatePicker ───────────────────────────────────────────────────────────────

interface DatePickerProps {
  value: string;
  onChange: (iso: string) => void;
  error?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, error }) => {
  const [text,     setText]     = useState(isoToDisplay(value));
  const [showCal,  setShowCal]  = useState(false);
  const [focused,  setFocused]  = useState(false);
  const [shake,    setShake]    = useState(false);
  const [localErr, setLocalErr] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  // Sync display text when value changes externally (e.g. snap from calendar)
  useEffect(() => { setText(isoToDisplay(value)); }, [value]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Don't validate if focus moved to the calendar panel
    if (wrapRef.current && wrapRef.current.contains(e.relatedTarget as Node)) return;
    setFocused(false);
    if (!text) { onChange(''); setLocalErr(''); return; }
    const parsed = parseDisplay(text);
    if (!parsed) {
      setLocalErr('Bitte Datum im Format TT.MM.JJJJ eingeben');
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    parsed.setHours(0,0,0,0);
    const snapped = isSelectable(parsed) ? parsed : nextSelectable(parsed);
    onChange(toIso(snapped));
    setLocalErr('');
  };

  const displayErr = localErr || error;
  const raised = focused || text.length > 0;

  return (
    <div className="calendar-wrapper" ref={wrapRef}>
      <motion.div
        className="floating-field"
        animate={shake ? { x: [0,-6,6,-4,4,0] } : { x: 0 }}
        transition={shake ? { duration: 0.35 } : {}}
      >
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onFocus={() => { setFocused(true); setShowCal(true); }}
          onClick={() => setShowCal(true)}
          onBlur={handleBlur}
          placeholder={focused ? 'TT.MM.JJJJ' : ''}
          style={displayErr ? { borderBottomColor: 'var(--color-brand)' } as React.CSSProperties : undefined}
        />
        <span className={`floating-label${raised ? ' raised' : ''}`}>
          Gewünschtes Datum <span className="req">*</span>
        </span>
        {displayErr && <div className="field-error">{displayErr}</div>}
      </motion.div>

      <AnimatePresence>
        {showCal && (
          <CustomCalendar
            selectedIso={value}
            onSelect={iso => {
              onChange(iso);
              setShowCal(false);
              setLocalErr('');
            }}
            onClose={() => setShowCal(false)}
            anchorRef={wrapRef}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── TimeSlotPicker ───────────────────────────────────────────────────────────

interface TimeSlotPickerProps {
  selectedDate: string;
  selectedTime: string;
  onChange: (t: string) => void;
  error?: string;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({ selectedDate, selectedTime, onChange, error }) => {
  if (!selectedDate) {
    return (
      <div style={{ marginBottom: '1.75rem' }}>
        <div className="ts-label">Gewünschte Uhrzeit <span className="req">*</span></div>
        <div className="ts-placeholder">Bitte zuerst ein Datum wählen</div>
      </div>
    );
  }

  const slots = isFridayDay(new Date(selectedDate + 'T12:00:00')) ? fridayHours : businessHours;

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div className="ts-label">Gewünschte Uhrzeit <span className="req">*</span></div>
      <div className="ts-grid">
        {slots.map(s => (
          <button
            key={s}
            type="button"
            className={`ts-pill${selectedTime === s ? ' ts-pill-selected' : ''}`}
            onClick={() => onChange(s)}
          >
            {s} Uhr
          </button>
        ))}
      </div>
      {error && <div className="field-error">{error}</div>}
    </div>
  );
};

// ─── ServicePills ─────────────────────────────────────────────────────────────

interface ServicePillsProps {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}

const ServicePills: React.FC<ServicePillsProps> = ({ value, onChange, error }) => (
  <div>
    <div className="svc-label">Art der Serviceleistung <span className="req">*</span></div>
    <div className="svc-pills">
      {serviceOptions.map(o => (
        <button
          key={o.value}
          type="button"
          className={`svc-pill${value === o.value ? ' svc-pill-selected' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
    {error && <div className="field-error" style={{ marginTop: '-1.25rem', marginBottom: '1rem' }}>{error}</div>}
  </div>
);

// ─── StepperHeader ────────────────────────────────────────────────────────────

const StepperHeader: React.FC<{ currentStep: number }> = ({ currentStep }) => (
  <>
    <div className="stepper-mobile-label">Schritt {currentStep} / 3</div>
    <div className="stepper-header">
      <div className="stepper-track">
        {STEPS.map((s, i) => {
          const n = i + 1;
          const isActive    = currentStep === n;
          const isCompleted = currentStep > n;
          const circleClass = isActive ? 'active' : isCompleted ? 'completed' : 'upcoming';
          const labelClass  = isActive ? 'active' : isCompleted ? 'completed' : '';
          return (
            <React.Fragment key={n}>
              <div className="stepper-step">
                <div className={`stepper-circle ${circleClass}`}>
                  {isCompleted ? '✓' : n}
                </div>
                <span className={`stepper-label ${labelClass}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`stepper-line ${isCompleted ? 'filled' : 'empty'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </>
);

// ─── Step 1 ───────────────────────────────────────────────────────────────────

interface StepProps {
  data: FormData;
  errors: FieldErrors;
  shakes: ShakeMap;
  set: (k: keyof FormData, v: string | boolean) => void;
  dir: number;
}

const Step1: React.FC<StepProps> = ({ data, errors, shakes, set, dir }) => {
  const v = makeStepVariants(dir);
  return (
    <motion.div key="step1" initial={v.initial} animate={v.animate} exit={v.exit}>
      <h2 className="step-title">Über Sie</h2>
      <CustomerToggle isNew={data.isFirstTimeCustomer} onChange={v2 => set('isFirstTimeCustomer', v2)} />

      <AnimatePresence mode="wait">
        {data.isFirstTimeCustomer ? (
          <motion.div
            key="neukunde"
            {...fieldGroupVariants}
            style={{ overflow: 'hidden' }}
          >
            <div className="field-row">
              <FloatingInput
                label="Vorname" required
                value={data.firstName} onChange={v2 => set('firstName', v2)}
                error={errors.firstName} shake={shakes.firstName}
              />
              <FloatingInput
                label="Nachname" required
                value={data.lastName} onChange={v2 => set('lastName', v2)}
                error={errors.lastName} shake={shakes.lastName}
              />
            </div>
            <FloatingInput
              label="E-Mail" required type="email"
              value={data.email} onChange={v2 => set('email', v2)}
              error={errors.email} shake={shakes.email}
            />
            <FloatingInput
              label="Telefon" required type="tel"
              value={data.phone} onChange={v2 => set('phone', v2)}
              error={errors.phone} shake={shakes.phone}
            />
          </motion.div>
        ) : (
          <motion.div
            key="bestandskunde"
            {...fieldGroupVariants}
            style={{ overflow: 'hidden' }}
          >
            <FloatingInput
              label="E-Mail oder Telefon" required
              value={data.bestandskundeContact}
              onChange={v2 => set('bestandskundeContact', v2)}
              error={errors.bestandskundeContact}
              shake={shakes.bestandskundeContact}
            />
            <p className="helper-text">
              Wir suchen Ihre Daten anhand Ihrer Kontaktdaten heraus.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Step 2 ───────────────────────────────────────────────────────────────────

const Step2: React.FC<StepProps> = ({ data, errors, shakes, set, dir }) => {
  const v = makeStepVariants(dir);
  return (
    <motion.div key="step2" initial={v.initial} animate={v.animate} exit={v.exit}>
      <h2 className="step-title">Ihr Fahrzeug &amp; Termin</h2>

      <div className="field-row">
        <FloatingInput
          label="Marke" required={data.isFirstTimeCustomer}
          value={data.vehicleMake} onChange={v2 => set('vehicleMake', v2)}
          error={errors.vehicleMake} shake={shakes.vehicleMake}
        />
        <FloatingInput
          label="Modell"
          value={data.vehicleModel} onChange={v2 => set('vehicleModel', v2)}
        />
      </div>
      <div className="field-row">
        <FloatingInput
          label="Baujahr"
          value={data.vehicleYear} onChange={v2 => set('vehicleYear', v2)}
        />
        <FloatingInput
          label="Kennzeichen" required={data.isFirstTimeCustomer}
          value={data.licensePlate} onChange={v2 => set('licensePlate', v2)}
          error={errors.licensePlate} shake={shakes.licensePlate}
        />
      </div>

      <hr className="step-divider" />

      <DatePicker
        value={data.preferredDate}
        onChange={iso => {
          if (iso !== data.preferredDate) set('preferredTime', '');
          set('preferredDate', iso);
        }}
        error={errors.preferredDate}
      />

      <TimeSlotPicker
        selectedDate={data.preferredDate}
        selectedTime={data.preferredTime}
        onChange={t => set('preferredTime', t)}
        error={errors.preferredTime}
      />

      <hr className="step-divider" />

      <ServicePills
        value={data.serviceType}
        onChange={v2 => set('serviceType', v2)}
        error={errors.serviceType}
      />

      <FloatingInput
        label="Zusätzliche Notizen"
        as="textarea"
        value={data.additionalNotes}
        onChange={v2 => set('additionalNotes', v2)}
        placeholder="Beschreiben Sie hier Ihr Anliegen oder geben Sie weitere Details an..."
      />
    </motion.div>
  );
};

// ─── Step 3 ───────────────────────────────────────────────────────────────────

interface Step3Props {
  data: FormData;
  onEdit: (step: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  dir: number;
}

const Step3: React.FC<Step3Props> = ({ data, onEdit, onSubmit, isSubmitting, dir }) => {
  const v = makeStepVariants(dir);
  const svcLabel = serviceOptions.find(o => o.value === data.serviceType)?.label || '—';

  return (
    <motion.div key="step3" initial={v.initial} animate={v.animate} exit={v.exit}>
      <h2 className="step-title">Bestätigung</h2>

      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-card-title">Ihre Daten</div>
          <button className="summary-edit" type="button" onClick={() => onEdit(1)}>Bearbeiten</button>
          <div className="summary-card-value">
            {data.isFirstTimeCustomer ? (
              <>
                <div>{data.firstName} {data.lastName}</div>
                <div>{data.email}</div>
                <div>{data.phone}</div>
              </>
            ) : (
              <div>{data.bestandskundeContact}</div>
            )}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-title">Ihr Fahrzeug</div>
          <button className="summary-edit" type="button" onClick={() => onEdit(2)}>Bearbeiten</button>
          <div className="summary-card-value">
            {(data.vehicleMake || data.vehicleModel) && (
              <div>{data.vehicleMake} {data.vehicleModel}</div>
            )}
            {data.vehicleYear  && <div>Baujahr {data.vehicleYear}</div>}
            {data.licensePlate && <div>Kennzeichen: {data.licensePlate}</div>}
            {!data.vehicleMake && !data.vehicleModel && !data.vehicleYear && !data.licensePlate && <div>—</div>}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-title">Terminwunsch</div>
          <button className="summary-edit" type="button" onClick={() => onEdit(2)}>Bearbeiten</button>
          <div className="summary-card-value">
            <div>{formatDateGerman(data.preferredDate)}</div>
            <div>{data.preferredTime} Uhr</div>
            <div>{svcLabel}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-title">Anliegen</div>
          <div className="summary-card-value" style={{ whiteSpace: 'pre-wrap' }}>
            {data.additionalNotes || '—'}
          </div>
        </div>
      </div>

      <div className="submit-area">
        <button
          type="button"
          className="btn-next"
          style={{ minWidth: '200px' }}
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? <><span className="apt-spin" /> Wird gesendet…</>
            : 'Termin anfragen →'}
        </button>
        <p className="legal-note">
          Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
        </p>
      </div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const initialForm: FormData = {
  isFirstTimeCustomer: true,
  firstName: '', lastName: '', email: '', phone: '',
  bestandskundeContact: '',
  vehicleMake: '', vehicleModel: '', vehicleYear: '', licensePlate: '',
  serviceType: '', preferredDate: '', preferredTime: '', additionalNotes: '',
};

const Appointment: React.FC = () => {
  const [step,        setStep]        = useState(1);
  const [dir,         setDir]         = useState(0);
  const [formData,    setFormData]    = useState<FormData>(initialForm);
  const [errors,      setErrors]      = useState<FieldErrors>({});
  const [shakes,      setShakes]      = useState<ShakeMap>({});
  const [isSubmitting,setIsSubmitting]= useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError,   setShowError]   = useState(false);

  const setField = useCallback((k: keyof FormData, v: string | boolean) => {
    setFormData(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: undefined }));
  }, []);

  const triggerShakes = (keys: (keyof FormData)[]) => {
    const m: ShakeMap = {};
    keys.forEach(k => { m[k] = true; });
    setShakes(m);
    setTimeout(() => setShakes({}), 450);
  };

  const validateStep1 = (): boolean => {
    const errs: FieldErrors = {};
    const bad: (keyof FormData)[] = [];
    if (formData.isFirstTimeCustomer) {
      if (!formData.firstName.trim()) { errs.firstName = 'Pflichtfeld'; bad.push('firstName'); }
      if (!formData.lastName.trim())  { errs.lastName  = 'Pflichtfeld'; bad.push('lastName'); }
      if (!formData.email.trim())     { errs.email     = 'Pflichtfeld'; bad.push('email'); }
      if (!formData.phone.trim())     { errs.phone     = 'Pflichtfeld'; bad.push('phone'); }
    } else {
      if (!formData.bestandskundeContact.trim()) {
        errs.bestandskundeContact = 'Pflichtfeld'; bad.push('bestandskundeContact');
      }
    }
    setErrors(errs);
    if (bad.length) { triggerShakes(bad); return false; }
    return true;
  };

  const validateStep2 = (): boolean => {
    const errs: FieldErrors = {};
    const bad: (keyof FormData)[] = [];
    if (formData.isFirstTimeCustomer) {
      if (!formData.vehicleMake.trim())   { errs.vehicleMake   = 'Pflichtfeld'; bad.push('vehicleMake'); }
      if (!formData.licensePlate.trim())  { errs.licensePlate  = 'Pflichtfeld'; bad.push('licensePlate'); }
    }
    if (!formData.preferredDate)  { errs.preferredDate = 'Bitte Datum wählen';          bad.push('preferredDate'); }
    if (!formData.preferredTime)  { errs.preferredTime = 'Bitte Uhrzeit wählen';        bad.push('preferredTime'); }
    if (!formData.serviceType)    { errs.serviceType   = 'Bitte Serviceleistung wählen'; bad.push('serviceType'); }
    setErrors(errs);
    if (bad.length) { triggerShakes(bad); return false; }
    return true;
  };

  const goTo = (target: number) => {
    setDir(target > step ? 1 : -1);
    setStep(target);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    goTo(step + 1);
  };

  const handleBack = () => goTo(step - 1);

  const handleSubmit = async () => {
    setShowError(false);
    setIsSubmitting(true);
    try {
      const payload = formData.isFirstTimeCustomer
        ? formData
        : { ...formData, email: formData.bestandskundeContact, phone: '' };
      const res = await fetch(APPOINTMENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setShowSuccess(true);
      setFormData(initialForm);
      setStep(1);
      setDir(1);
    } catch {
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageTransition>
      <div className="apt-page">
        <Container>
          {showSuccess && (
            <div className="apt-success">
              Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet. Wir werden uns in Kürze bei Ihnen melden.
            </div>
          )}
          {showError && (
            <div className="apt-error">
              Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.
            </div>
          )}

          <StepperHeader currentStep={step} />

          <div className="apt-card">
            <form onSubmit={e => e.preventDefault()}>
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <Step1 key="s1" data={formData} errors={errors} shakes={shakes} set={setField} dir={dir} />
                )}
                {step === 2 && (
                  <Step2 key="s2" data={formData} errors={errors} shakes={shakes} set={setField} dir={dir} />
                )}
                {step === 3 && (
                  <Step3 key="s3" data={formData} onEdit={goTo} onSubmit={handleSubmit} isSubmitting={isSubmitting} dir={dir} />
                )}
              </AnimatePresence>

              <div className="step-nav">
                <button
                  type="button"
                  className="btn-back"
                  onClick={handleBack}
                  style={{ visibility: step === 1 ? 'hidden' : 'visible' }}
                >
                  ← Zurück
                </button>
                {step < 3 && (
                  <button type="button" className="btn-next" onClick={handleNext}>
                    Weiter →
                  </button>
                )}
              </div>
            </form>
          </div>
        </Container>
      </div>
    </PageTransition>
  );
};

export default Appointment;
